import { inject, Injectable } from "@angular/core";
import { DeviceType } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { isGroupedOption } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { FamilyVariantResult } from "@project/monitoring/monitoring-family-wizard.utils";
import { forkJoin, map, Observable, of } from "rxjs";

export interface OptionDisplay {
  option: FamilyStepOption;
  displayName: string;
  imageId?: string;
  description?: string;
  piSheetURL?: string;
  configurationURL?: string;
  matNr?: string;
  family?: Family;
  familyVariant?: FamilyVariantResult;
  deviceType?: DeviceType;
  deviceTypes?: DeviceType[];
}

@Injectable({
  providedIn: "root",
})
export class OptionDisplayService {
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);

  createOptionDisplay(option: FamilyStepOption, category: MonitoringCategory, family?: Family): Observable<OptionDisplay> {
    if (isGroupedOption(option)) {
      const displayName = option.groupName?.trim() || option.devices.join(", ");
      const baseDisplay: OptionDisplay = {
        option,
        displayName,
        imageId: this.toCantoUrl(option.groupImageCantoID),
        description: option.description ?? "",
        piSheetURL: option.piSheetURL ?? "",
        configurationURL: option.configurationURL ?? "",
        matNr: undefined,
        family,
      };

      // Resolve all referenced device types so category filters also work for grouped options.
      if (option.devices.length > 0) {
        return forkJoin(
          option.devices.map((deviceId) =>
            this.monitoringProductService.loadDeviceFromDB(deviceId, category, this.productService.getFilterCountry()),
          ),
        ).pipe(
          map((devices) => {
            const deviceTypes = Array.from(
              new Set(devices.map((device) => device?.deviceType).filter((type): type is DeviceType => !!type)),
            );
            return {
              ...baseDisplay,
              deviceType: deviceTypes[0],
              deviceTypes,
            };
          }),
        );
      }

      return of(baseDisplay);
    }

    return this.monitoringProductService
      .loadDeviceFromDB(option.devices[0], category, this.productService.getFilterCountry())
      .pipe(
        map((device) => {
          if (!device) {
            const fallbackName = option.groupName || option.devices[0];
            return {
              option,
              displayName: fallbackName,
              imageId: this.toCantoUrl(option.groupImageCantoID),
              description: option.description ?? "",
              piSheetURL: option.piSheetURL ?? "",
              configurationURL: option.configurationURL ?? "",
              matNr: option.devices[0],
              family,
            };
          }
          return {
            option,
            displayName: device.name,
            imageId: this.toCantoUrl(device.productImageCantoID),
            description: device.description,
            piSheetURL: device.piSheetURL,
            configurationURL: device.configurationURL,
            matNr: device.matNr,
            family,
            deviceType: device.deviceType as DeviceType,
            deviceTypes: [device.deviceType as DeviceType],
          };
        }),
      );
  }

  private toCantoUrl(cantoId?: string | null) {
    if (!cantoId) return undefined;
    return ProductImage.getCantoSourceUrl(cantoId);
  }
}
